import { EventClickArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import EmptyIcon from 'src/assets/images/NoDaypartEnabled.svg';
import { Calendar } from 'src/components/Calendar/Calendar';
import {
  ECalendarEventType,
  ECalendarViewsId,
} from 'src/components/Calendar/Calendar.interface';
import { CalendarHeader } from 'src/components/Calendar/CalendarHeader/CalendarHeader';
import { PublicationModal } from 'src/components/modal';
import { CALENDAR_HEADER_HEIGHT, DEFAULT_POSITION_ALL } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  ICalendarView,
  IDaypartsDataRecord,
  IScheduleEvent,
} from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/CalendarView.interface';
import {
  NoDaypartImg,
  NoDaypartWrapper,
  Spinner,
} from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/CalendarView.styled';
import { EventContent } from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/eventContent';
import { EventContentSidebar } from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/eventContentSidebar/EventContentSidebar';
import { ResourceContent } from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/resourceContent';
import { useGetAllDayparts, useModal, useParamQuery } from 'src/hooks';
import { IDaypart, PublicationTypes } from 'src/interfaces';
import {
  adjustEndDate,
  createEventSource,
  createResourceSource,
  getSidebarDetailsData,
  lang,
  ScheduleDetailsParams,
  useUserRole,
} from 'src/utils';

import {
  CalendarEvent,
  DefaultAssemblyEvent,
  ScheduleComponentEvent,
} from './eventContentSidebar/EventContentSidebar.interface';

export const CalendarView = (props: ICalendarView) => {
  const [selectedEventData, setSelectedEventData] = useState<CalendarEvent>();
  const {
    locationGroup,
    isCalendarViewGroup,
    shouldShowCalendar,
    projectConfig,
    deleteBundleAction,
    selectedDaypartId,
    handleSelectedDaypartIdChange,
    locationData,
    locationsCount,
  } = props;

  let { locationGroupId: locationGroupCode, locationCode } = useParams();

  const { getParamsValue } = useParamQuery();
  const groupIDs = getParamsValue('groupID').filter(
    (id) => id && id !== 'undefined',
  );
  const individualLocationCode = groupIDs[0] ?? '';

  const locationGroupId = isCalendarViewGroup
    ? locationGroupCode ?? ''
    : individualLocationCode;

  const { data: daypartOptions, isLoading: isLoadingDayparts } =
    useGetAllDayparts();

  const showPublicationModal = useModal();

  const { isOwnerOperator } = useUserRole();

  const spinnerRef = useRef<HTMLDivElement | null>(null);
  const calendarRef = useRef<FullCalendar | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [position, setPosition] = useState<string>(DEFAULT_POSITION_ALL);

  //NOTE: This is set to monthly view as default for calendar in the UI.
  const [viewType, setViewType] = useState<string>(
    ECalendarViewsId.resourceTimelineMonth,
  );
  const [currentSidebarDetailsDataParams, setCurrentSidebarDetailsDataParams] =
    useState<ScheduleDetailsParams>();

  //NOTE: This method is purely needed to recalculate the calendar size for an instance as a result of bug from the library
  // More details on - https://github.com/fullcalendar/fullcalendar/issues/6082
  const handleSizeUpdate = () => {
    setTimeout(() => {
      const calendarApi = calendarRef.current?.getApi?.();
      if (calendarApi) {
        // Recalculate the size after a slight delay
        calendarApi.updateSize();
      }
    }, 500);
  };

  useEffect(() => {
    //Reset the calendar when switching between location groups.
    handleSizeUpdate();
    setPosition(DEFAULT_POSITION_ALL);
    handleSelectedDaypartIdChange('1');
    setViewType(ECalendarViewsId.resourceTimelineMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationGroupId]);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEventData(undefined);
  };

  const handleEventClick = async (arg: EventClickArg) => {
    let sidebarData: CalendarEvent;
    const currentScheduleId = arg.event.extendedProps?.scheduleId;
    const currentAssemblyId = arg.event.extendedProps?.assemblyId;
    const currentLocationCode = String(locationCode ?? '');
    const currentPosition = String(position ?? '');

    const isDefaultAssembly =
      arg.event.extendedProps.eventType === ECalendarEventType.defaultAssembly;
    const payload = {
      scheduleId: currentScheduleId ? String(currentScheduleId) : undefined,
      assemblyId: currentAssemblyId ? String(currentAssemblyId) : undefined,
      locationCode: currentLocationCode,
      position: currentPosition,
    };
    const response = await getSidebarDetailsData(payload);
    if (isDefaultAssembly) {
      sidebarData = {
        ...response,

        //NOTE: Add this to the api data as , api doesnt provide this info when assembly_id is passed
        start: arg.event.start,
        modifiedDate: arg.event.extendedProps.modifiedDate,
        // FullCalendar treats end dates as exclusive, but default assemblies should include the end date
        end: adjustEndDate(
          arg.event.end ?? undefined,
          arg.event.extendedProps?.adjustedEnd,
        ),
      } as DefaultAssemblyEvent;
    } else {
      sidebarData = {
        ...response,
        scheduleId: currentScheduleId as number,
      } as ScheduleComponentEvent;
    }

    setSelectedEventData(sidebarData);
    setCurrentSidebarDetailsDataParams(payload);
    setDrawerOpen(true);
  };

  const eventData = useMemo(
    () => [
      createEventSource({
        selectedDaypartId,
        locationGroupId,
        viewType,
        position,
        location_code: locationCode || 'null',
      }),
    ],
    [locationCode, locationGroupId, position, selectedDaypartId, viewType],
  );

  const resourceData = useMemo(() => {
    if (!locationGroupId || !selectedDaypartId) return [];

    return createResourceSource({ selectedDaypartId, locationGroupId });
  }, [locationGroupId, selectedDaypartId]);

  const refreshEventData = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  };

  const refreshSidebarData = async () => {
    if (!currentSidebarDetailsDataParams) return;

    const response = await getSidebarDetailsData(
      currentSidebarDetailsDataParams,
    );
    if (!response) {
      setSelectedEventData(undefined);
      setDrawerOpen(false);

      return;
    }

    setSelectedEventData((prev) => {
      const isDefaultAssembly =
        prev?.eventType === ECalendarEventType.defaultAssembly;

      if (isDefaultAssembly) {
        return {
          ...response,
          start: (prev as DefaultAssemblyEvent)?.start,
          modifiedDate: (prev as DefaultAssemblyEvent)?.modifiedDate,
          end: (prev as DefaultAssemblyEvent)?.end,
        } as DefaultAssemblyEvent;
      }

      return {
        ...response,
        scheduleId: (prev as ScheduleComponentEvent)?.scheduleId,
      } as ScheduleComponentEvent;
    });
  };

  const onViewChange = (viewType: string) => {
    setViewType(viewType);
    props.viewTypeChangeCallback?.(viewType);
  };

  return (
    <>
      <Spinner ref={spinnerRef}>Loading...</Spinner>
      <CalendarHeader
        calendarRef={calendarRef}
        layoutType={locationGroup?.layoutType?.name}
        projectConfig={projectConfig}
        position={position}
        onPositionChange={setPosition}
        onDaypartChange={handleSelectedDaypartIdChange}
        onViewChange={onViewChange}
        daypartOptions={daypartOptions as IDaypart[]}
        isLoadingDayparts={isLoadingDayparts}
        selectedDaypartId={selectedDaypartId}
        viewType={viewType}
        shouldDisableDropdown={!shouldShowCalendar}
      />
      <Box height={`calc(100% - ${CALENDAR_HEADER_HEIGHT}px)`}>
        {!shouldShowCalendar ? (
          <NoDaypartWrapper>
            <NoDaypartImg
              src={EmptyIcon}
              alt="emptyIcon"
              data-testid="emptyIcon"
              draggable={false}
            />
            <Typography variant="body2">{lang.noDaypartsEnabled}</Typography>
          </NoDaypartWrapper>
        ) : (
          <Calendar
            eventClick={handleEventClick}
            ref={calendarRef}
            defaultAllDayEventDuration={{ year: 3 }}
            eventSources={eventData}
            resources={resourceData}
            loading={(isLoading) => {
              if (spinnerRef.current) {
                isLoading
                  ? spinnerRef.current.classList.add('active')
                  : spinnerRef.current.classList.remove('active');
              }
            }}
            headerToolbar={false}
            resourceAreaWidth={'118px'}
            eventOrder={['eventType', '-modifiedDate']}
            resourceOrder={'title'}
            resourceAreaHeaderContent={false}
            resourceLabelContent={(arg) => (
              <ResourceContent
                resource={arg.resource as unknown as IDaypartsDataRecord}
              />
            )}
            eventContent={(arg) => (
              <EventContent
                event={arg.event as unknown as IScheduleEvent}
                selectedEvent={selectedEventData}
                isCalendarViewGroup={isCalendarViewGroup}
                refreshEventData={refreshEventData}
                showPublicationModal={showPublicationModal}
              />
            )}
            previewClick={(date: string) => {
              const previewPath = PATHS.SCHEDULE_PREVIEW(
                String(locationGroupId),
                undefined,
                String(props.selectedDaypartId),
                date,
              );

              const previewUrl = `${window.location.origin}${window.location.pathname}#${previewPath}`;
              window.open(previewUrl, '_blank');
            }}
          />
        )}
      </Box>

      <EventContentSidebar
        selectedEvent={selectedEventData}
        open={drawerOpen}
        close={handleDrawerClose}
        locationGroupName={locationGroup?.name}
        channelName={locationGroup?.channel_name}
        locationGroupId={locationGroupId}
        // the only variable to differentiate between LG and individual location (both CM and OO) calendar view
        isLocationGroupView={!!locationGroupCode}
        locationCode={locationCode}
        locationName={locationData?.name}
        refreshEventData={refreshEventData}
        refreshSidebarData={refreshSidebarData}
        showPublicationModal={showPublicationModal}
        position={position}
        currentSidebarDetailsDataParams={currentSidebarDetailsDataParams}
        deleteBundleAction={deleteBundleAction}
        daypartId={selectedDaypartId}
        locationsCount={locationsCount}
      />

      {showPublicationModal.open && (
        <PublicationModal
          data-testid="publication-modal"
          open={showPublicationModal.open}
          publicationType={
            isOwnerOperator ? PublicationTypes.OVERRIDES : PublicationTypes.FULL
          }
          modalSize="sm"
          {...(locationGroupCode && {
            locationGroupIDs: [Number(locationGroupCode)],
          })}
          {...(locationCode && {
            locationCodes: [locationCode],
          })}
          onClose={showPublicationModal.onCloseModal}
        />
      )}
    </>
  );
};
